import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ILoyaltyServiceCreateOrderSelectionInput,
  LoyaltyOstType,
  LoyaltyPromotionType,
  LoyaltyServiceOstType,
  useCreateOrderSelectionByMultipleTypeMutation,
  useLoyaltyServiceCreateOrderSelectionMutation,
} from 'generated/graphql-gateway';
import { useSyncStorageObject } from 'hooks/use-local-storage-state/use-sync-storage-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyIncentivesEvaluation } from 'state/loyalty/hooks/use-loyalty-incentive-evaluation';
import { SyncStorageKey } from 'utils/environment/sync-storage';
import logger from 'utils/logger';

import {
  IGetNewShortCodeParams,
  IShortCodeData,
  IUserShortCode,
  OrderSelectionBackend,
  ShortCodeState,
} from './types';
import { differenceMsFromNow, isExpired } from './utils';

type OrderSelectionCreationPayload = {
  readonly expirationTime: string;
  readonly key: string;
  readonly ostType: LoyaltyOstType | LoyaltyServiceOstType;
};

/**
 * useShortCode holds otp short-code and its state.
 */
export const useShortCode = (): IUserShortCode => {
  // ENABLE_LOYALTY_SERVICE_ORDER_SELECTION is its own independent switch for
  // whether order-selection creation uses loyalty-service vs the legacy
  // mutation — it's deliberately unrelated to ENABLE_LOYALTY_SERVICE_OFFERS/
  // REWARDS (those control the offers/rewards *lists*, not redemption). Do
  // not derive this from offers/rewards eligibility. See BKPE-27933.
  const enableLoyaltyServiceOrderSelection = Boolean(
    useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_SERVICE_ORDER_SELECTION)
  );
  const { evaluateLoyaltyIncentives } = useLoyaltyIncentivesEvaluation();
  const generateCodeOtpTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const abortControllerRef = useRef<AbortController>(undefined);
  const [shortCodeLoading, setShortCodeLoading] = useState<boolean>(false);
  const [shortCodeStorageItem, setShortCodeData, clearShortCodeData] = useSyncStorageObject<
    IShortCodeData
  >(SyncStorageKey.IN_RESTAURANT_REDEMPTION_SHORT_CODE, {});
  const { shortCode, expiry, backend: storedBackend } = shortCodeStorageItem || {};
  // Codes created before this field existed have no stored backend; they can
  // only have been created via the legacy mutation, so default to that.
  const shortCodeBackend: OrderSelectionBackend = storedBackend ?? 'legacy';
  const [shortCodeState, setShortCodeState] = useState<ShortCodeState>(() =>
    shortCode ? ShortCodeState.Pending : ShortCodeState.None
  );
  const shouldEvaluateIncentivesRef = useRef<boolean>(undefined);
  // evaluate incentives only if the short code is expired to avoid unnecessary requests
  shouldEvaluateIncentivesRef.current = shortCodeState === ShortCodeState.Expired;

  const setRequestError = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.RequestError);
  }, [clearShortCodeData]);

  const setShortCodeClaimed = useCallback(() => {
    setShortCodeState(ShortCodeState.Claimed);
  }, []);

  const setShortCodeExpired = useCallback(() => {
    setShortCodeState(ShortCodeState.Expired);
  }, []);

  const resetShortCode = useCallback(() => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.None);
    setShortCodeLoading(false);
  }, [clearShortCodeData]);

  const setIncentiveValidationError = useCallback(() => {
    setShortCodeState(ShortCodeState.OfferValidationError);
  }, [setShortCodeState]);

  // Records which backend actually created the order selection — derived
  // from which mutation's `onCompleted` fired below, not from re-reading
  // `enableLoyaltyServiceOrderSelection`. That flag/experiment-derived value
  // can change between the call and the response (e.g. the render-ready
  // experiment resolves asynchronously and starts `false`), so recomputing it
  // here could disagree with which mutation was actually sent. Persisting the
  // backend the code was truly created on keeps use-short-code-poll-status.ts
  // polling the right endpoint regardless of later eligibility changes.
  // See BKPE-27933.
  const handleOrderSelectionCreated = useCallback(
    (
      orderSelections: ReadonlyArray<OrderSelectionCreationPayload>,
      backend: OrderSelectionBackend
    ) => {
      const otpCodeData = orderSelections.find(el => el.ostType === LoyaltyOstType.OTP);

      if (!otpCodeData) {
        return;
      }
      const { key: newShortCode, expirationTime } = otpCodeData;

      setShortCodeData({
        shortCode: newShortCode,
        expiry: expirationTime,
        backend,
      });
      setShortCodeState(ShortCodeState.Pending);
    },
    [setShortCodeData]
  );

  const [generateOrderSelection] = useCreateOrderSelectionByMultipleTypeMutation({
    onCompleted: ({ loyaltyCreateOrderSelectionByMultipleType }) => {
      handleOrderSelectionCreated(loyaltyCreateOrderSelectionByMultipleType, 'legacy');
    },
    onError: () => {
      setRequestError();
    },
  });
  const [generateLoyaltyServiceOrderSelection] = useLoyaltyServiceCreateOrderSelectionMutation({
    onCompleted: ({ loyaltyServiceCreateOrderSelection }) => {
      handleOrderSelectionCreated(loyaltyServiceCreateOrderSelection, 'loyalty-service');
    },
    onError: () => {
      setRequestError();
    },
  });

  /**
   * getNewShortCode works as a debounce function in order to prevent running
   * multiple mutations at the same time
   */
  const getNewShortCode = useCallback(
    ({
      inRestaurantOrder,
      loyaltyId,
      restaurantId,
      serviceMode,
      appliedOffers,
    }: IGetNewShortCodeParams) => {
      if (generateCodeOtpTimeoutRef.current) {
        clearTimeout(generateCodeOtpTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setShortCodeLoading(true);
      generateCodeOtpTimeoutRef.current = global.setTimeout(async () => {
        const controller = window.AbortController ? new window.AbortController() : undefined;
        abortControllerRef.current = controller;

        // flag to avoid set the state if the request is aborted
        let isRequestAborted = false;
        if (controller?.signal) {
          controller.signal.onabort = () => {
            isRequestAborted = true;
          };
        }

        try {
          // Incentive validations
          let hasValidationErrors = false;
          if (shouldEvaluateIncentivesRef.current && appliedOffers.length) {
            const evaluationResult = await evaluateLoyaltyIncentives({
              appliedIncentives: appliedOffers,
              incentiveType: LoyaltyPromotionType.OFFER,
              loyaltyId,
              storeId: restaurantId,
              serviceMode,
            });

            // skip validation if the request was aborted to prevent set state
            if (!isRequestAborted && evaluationResult) {
              hasValidationErrors = appliedOffers.some(({ id }: any) => !!evaluationResult[id]);

              if (hasValidationErrors) {
                setIncentiveValidationError();
              }
            }
          }

          if (!hasValidationErrors) {
            if (enableLoyaltyServiceOrderSelection) {
              await generateLoyaltyServiceOrderSelection({
                variables: {
                  input: {
                    inRestaurantOrder: inRestaurantOrder as ILoyaltyServiceCreateOrderSelectionInput['inRestaurantOrder'],
                    restaurantId,
                    ostTypes: [LoyaltyServiceOstType.LOYALTY_ID, LoyaltyServiceOstType.OTP],
                  },
                },
                context: {
                  fetchOptions: {
                    signal: controller?.signal,
                  },
                },
              });
            } else {
              await generateOrderSelection({
                variables: {
                  inRestaurantOrder,
                  restaurantId,
                  loyaltyId,
                  ostTypes: [LoyaltyOstType.LOYALTY_ID, LoyaltyOstType.OTP],
                },
                context: {
                  fetchOptions: {
                    signal: controller?.signal,
                  },
                },
              });
            }
          }
        } catch (e) {
          logger.error(`Error generating a short code: ${e}`);
        } finally {
          generateCodeOtpTimeoutRef.current = undefined;
          setShortCodeLoading(false);
        }
      }, 250);
    },
    [
      enableLoyaltyServiceOrderSelection,
      evaluateLoyaltyIncentives,
      generateLoyaltyServiceOrderSelection,
      generateOrderSelection,
      setIncentiveValidationError,
    ]
  );

  useEffect(() => {
    if (!expiry) {
      return;
    } else if (isExpired(expiry)) {
      setShortCodeExpired();
      return;
    }

    const codeDurationMilliseconds = differenceMsFromNow(expiry);
    const timeoutId = setTimeout(setShortCodeExpired, codeDurationMilliseconds);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiry, setShortCodeExpired]);

  return {
    resetShortCode,
    getNewShortCode,
    setShortCodeClaimed,
    setShortCodeExpired,
    shortCode,
    shortCodeBackend,
    shortCodeLoading,
    shortCodeState,
  };
};
